import React, { Fragment, useState } from 'react';
import { Chip, FormControl, Popover, TextField } from '@material-ui/core';
import dayjs from 'dayjs';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { CustomDateSelector } from 'pages/DashboardPage/components/LargeGraphPopup';
import { MenuItemPopOver, TitlePopOver } from 'modules/Sty';
import { convertSnakeCase, makeKey } from 'modules/Utils';
import ReactDatePicker from 'react-datepicker';
import DropdownArrow from 'components/shareable/DropdownArrow';
import ButtonSymphony from 'elements/ButtonSymphony';
import useScreen from 'Hooks/useScreen';

// The filter options available to a user
const INVOICE_FILTERS = [
    { name: 'brand', title: 'Brand' },
    { name: 'status', title: 'Status' },
    { name: 'date', title: 'Date' },
    { name: 'amount', title: 'Amount' },
];

const INVOICE_STATUS_TYPES = [
    { name: 'paid', title: 'Paid' },
    { name: 'open', title: 'Open' },
    { name: 'draft', title: 'Draft' },
    { name: 'void', title: 'Void' },
];

type FilterTypeDate = 'all_time' | 'in_between' | 'before' | 'after' | 'last_days';
type FilterTypeAmount = 'no_amount_filter' | 'equal' | 'greater_than' | 'less_than' | 'between_two_numbers';

interface InvoiceFilter {
    brands: string[];
    statuses: string[];
    dateModifier: FilterTypeDate;
    startDate: string;
    endDate: string;
    filterDays: string;
    amountModifier: FilterTypeAmount;
    amountValue1: string;
    amountValue2: string;
}

interface InvoiceFilterOptionsProps {
    filter: InvoiceFilter;
    onFilterChange: (filter: InvoiceFilter) => void;
    onApplyFilters: () => void;
    availableBrands?: Array<{ id: string; name: string }>;
    isLoading?: boolean;
}

const InvoiceFilterOptions: React.FC<InvoiceFilterOptionsProps> = ({
    filter,
    onFilterChange,
    onApplyFilters,
    availableBrands = [],
    isLoading = false
}) => {
    const { mobileView } = useScreen();
    
    const [openPopoverIndex, setOpenPopoverIndex] = useState<number | null>(null);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);

    const applyFilters = () => {
        handleClosePopover();
        onApplyFilters();
    };

    const removeFilter = () => {
        const filterLocal = { ...filter };

        switch (openPopoverIndex) {
            case 0: // brand
                filterLocal.brands = [];
                break;
            case 1: // status
                filterLocal.statuses = [];
                break;
            case 2: // date
                filterLocal.dateModifier = 'all_time';
                filterLocal.startDate = dayjs().subtract(30, 'days').format('YYYY-MM-DD');
                filterLocal.endDate = dayjs().format('YYYY-MM-DD');
                filterLocal.filterDays = '30';
                break;
            case 3: // amount
                filterLocal.amountModifier = 'no_amount_filter';
                filterLocal.amountValue1 = '';
                filterLocal.amountValue2 = '';
                break;
            default:
                break;
        }

        onFilterChange(filterLocal);
    };

    const handleSetFilterBrands = (brandId: string) => {
        const brandsLocal = [...filter.brands];
        const currentIndex = brandsLocal.findIndex(b => b === brandId);

        if (currentIndex === -1) {
            brandsLocal.push(brandId);
        } else {
            brandsLocal.splice(currentIndex, 1);
        }

        onFilterChange({
            ...filter,
            brands: brandsLocal
        });
    };

    const handleSetFilterStatuses = (status: string) => {
        const statusesLocal = [...filter.statuses];
        const currentIndex = statusesLocal.findIndex(s => s === status);

        if (currentIndex === -1) {
            statusesLocal.push(status);
        } else {
            statusesLocal.splice(currentIndex, 1);
        }

        onFilterChange({
            ...filter,
            statuses: statusesLocal
        });
    };

    const handleSetFilterDateModifier = (event: SelectChangeEvent) => {
        const dateModifier: FilterTypeDate = event.target.value as FilterTypeDate;
        onFilterChange({
            ...filter,
            dateModifier
        });
    };

    const handleSetAmount = (event: SelectChangeEvent) => {
        const amountModifier: FilterTypeAmount = event.target.value as FilterTypeAmount;
        onFilterChange({
            ...filter,
            amountModifier
        });
    };

    const handleChangeFilterDays = (event: React.ChangeEvent<HTMLInputElement>) => {
        const days = event.target.value.replace(/\D+/g, '');
        onFilterChange({
            ...filter,
            filterDays: days
        });
    };

    const handleChangeAmountValue1 = (event: React.ChangeEvent<HTMLInputElement>) => {
        const amount1 = event.target.value.replace(/\D+/g, '');
        onFilterChange({
            ...filter,
            amountValue1: amount1
        });
    };

    const handleChangeAmountValue2 = (event: React.ChangeEvent<HTMLInputElement>) => {
        const amount2 = event.target.value.replace(/\D+/g, '');
        onFilterChange({
            ...filter,
            amountValue2: amount2
        });
    };

    const isFilterActive = (filterName: string) => {
        switch (filterName) {
            case 'brand':
                return filter.brands.length;
            case 'status':
                return filter.statuses.length;
            case 'date':
                return filter.dateModifier !== 'all_time';
            case 'amount':
                return filter.amountModifier !== 'no_amount_filter';
            default:
                return null;
        }
    };

    const getFilterResultString = (filterOption: { name: string; title: string }) => {
        let filterResultString = null;
        const darkFont = { color: '#333' };

        switch (filterOption.name) {
            case 'brand':
                if (filter.brands.length > 0) {
                    const selectedBrandNames = filter.brands.map(brandId => {
                        const brand = availableBrands.find(b => b.id === brandId);
                        return brand?.name || brandId;
                    });
                    const firstTwoBrands = selectedBrandNames.slice(0, 2);
                    const remainingCount = selectedBrandNames.length - 2;
                    
                    if (remainingCount > 0) {
                        filterResultString = (
                            <>
                                Brand: <span style={darkFont}>{firstTwoBrands.join(', ')}</span> and {remainingCount} more
                            </>
                        );
                    } else {
                        filterResultString = (
                            <>
                                Brand: <span style={darkFont}>{firstTwoBrands.join(', ')}</span>
                            </>
                        );
                    }
                } else {
                    filterResultString = <>Brand</>;
                }
                break;
            case 'status':
                filterResultString = (
                    <>
                        Status {filter.statuses.length ? (
                            <span style={darkFont}>: {filter.statuses.map(s => convertSnakeCase(s)).join(', ')}</span>
                        ) : null}
                    </>
                );
                break;
            case 'date':
                const dateModifierString = convertSnakeCase(filter.dateModifier);
                const startDateString = dayjs(filter.startDate).format('MMM D, YYYY').toString();
                const endDateString = dayjs(filter.endDate).format('MMM D, YYYY').toString();
                
                if (filter.dateModifier === 'in_between') {
                    filterResultString = (
                        <>
                            Date Between: <span style={darkFont}>{startDateString} - {endDateString}</span>
                        </>
                    );
                } else if (filter.dateModifier === 'before' || filter.dateModifier === 'after') {
                    filterResultString = (
                        <>
                            Date {dateModifierString}: <span style={darkFont}>{startDateString}</span>
                        </>
                    );
                } else if (filter.dateModifier === 'last_days') {
                    filterResultString = (
                        <>
                            Last <span style={darkFont}>{filter.filterDays} days</span>
                        </>
                    );
                } else {
                    filterResultString = <>Date</>;
                }
                break;
            case 'amount':
                const filterAmountString = convertSnakeCase(filter.amountModifier);
                
                if (filter.amountModifier === 'equal') {
                    filterResultString = (
                        <>
                            Amount <span style={darkFont}>{filterAmountString} to: ${filter.amountValue1 || '0'}</span>
                        </>
                    );
                } else if (filter.amountModifier === 'greater_than' || filter.amountModifier === 'less_than') {
                    filterResultString = (
                        <>
                            Amount <span style={darkFont}>{filterAmountString}: ${filter.amountValue1 || '0'}</span>
                        </>
                    );
                } else if (filter.amountModifier === 'between_two_numbers') {
                    filterResultString = (
                        <>
                            Amount Between: <span style={darkFont}>${filter.amountValue1 || '0'} - ${filter.amountValue2 || '0'}</span>
                        </>
                    );
                } else {
                    filterResultString = <>Amount</>;
                }
                break;
            default:
                filterResultString = null;
                break;
        }

        return filterResultString;
    };

    const renderCustomDatesPicker = () => {
        if (filter.dateModifier === 'all_time') {
            return null;
        }

        if (filter.dateModifier === 'last_days') {
            return (
                <TextField
                    label="Last X Days"
                    variant="outlined"
                    value={filter.filterDays}
                    onChange={handleChangeFilterDays}
                    style={{ marginBottom: '10px' }}
                    placeholder="Enter a number..."
                />
            );
        }

        return (
            <>
                {filter.dateModifier === 'in_between' && <p className="mr-1">From:</p>}
                <ReactDatePicker
                    filterDate={(date: Date) => {
                        return dayjs(date) < dayjs(filter.endDate).subtract(1, 'day');
                    }}
                    selected={dayjs(filter.startDate).toDate()}
                    onChange={(date) => {
                        const filterLocal = { ...filter };
                        filterLocal.startDate = dayjs(date).format('YYYY-MM-DD');
                        onFilterChange(filterLocal);
                    }}
                    customInput={<CustomDateSelector />}
                    onCalendarOpen={() => setIsDatePickerOpen(true)}
                    onCalendarClose={() => setIsDatePickerOpen(false)}
                />
                {filter.dateModifier === 'in_between' && (
                    <>
                        <p className="mr-1">To:</p>
                        <ReactDatePicker
                            minDate={dayjs(filter.startDate).add(1, 'day').toDate()}
                            selected={dayjs(filter.endDate).toDate()}
                            onChange={(date) => {
                                const filterLocal = { ...filter };
                                filterLocal.endDate = dayjs(date).format('YYYY-MM-DD');
                                onFilterChange(filterLocal);
                            }}
                            customInput={<CustomDateSelector />}
                        />
                    </>
                )}
            </>
        );
    };

    const renderBrandFilter = () => {
        return (
            <div className="brandFilterContainer" style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 340 }}>
                <TitlePopOver>Filter by Brand</TitlePopOver>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {availableBrands.map(brand => (
                        <label key={brand.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={filter.brands.includes(brand.id)}
                                onChange={() => handleSetFilterBrands(brand.id)}
                            />
                            <span>{brand.name}</span>
                        </label>
                    ))}
                </div>
            </div>
        );
    };

    const renderStatusFilter = () => {
        return (
            <div className="statusFilterContainer" style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 340 }}>
                <TitlePopOver>Filter by Status</TitlePopOver>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {INVOICE_STATUS_TYPES.map(status => (
                        <label key={status.name} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={filter.statuses.includes(status.name)}
                                onChange={() => handleSetFilterStatuses(status.name)}
                            />
                            <span>{status.title}</span>
                        </label>
                    ))}
                </div>
            </div>
        );
    };

    const renderDateFilter = () => {
        return (
            <div className="dateFilter" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                maxWidth: 340,
                height: isDatePickerOpen ? '400px' : 'auto',
                overflow: isDatePickerOpen ? 'hidden' : 'visible'
            }}>
                <TitlePopOver>Select Date Range</TitlePopOver>
                <FormControl fullWidth>
                    <Select
                        labelId="filter-select-label"
                        id="filter-select-date"
                        value={filter.dateModifier}
                        label="Date Filter"
                        onChange={handleSetFilterDateModifier}
                        sx={{ fontFamily: 'DIN' }}
                        IconComponent={() =>
                            <DropdownArrow
                                marginRight={10}
                                width={24}
                                height={24}
                                direction="down"
                            />
                        }
                    >
                        <MenuItemPopOver value="all_time">All-Time</MenuItemPopOver>
                        <MenuItemPopOver value="in_between">In-Between</MenuItemPopOver>
                        <MenuItemPopOver value="before">Before</MenuItemPopOver>
                        <MenuItemPopOver value="after">After</MenuItemPopOver>
                        <MenuItemPopOver value="last_days">Last Days</MenuItemPopOver>
                    </Select>
                </FormControl>

                <div className="flex items-center justify-center">
                    {renderCustomDatesPicker()}
                </div>
            </div>
        );
    };

    const renderAmountFilter = () => {
        return (
            <div className="amountFilterContainer" style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 340 }}>
                <TitlePopOver>Filter by Amount</TitlePopOver>
                <FormControl fullWidth style={{ fontFamily: 'DIN' }}>
                    <Select
                        labelId="filter-select-label"
                        id="filter-select-amount"
                        onChange={handleSetAmount}
                        value={filter.amountModifier}
                        sx={{ fontFamily: 'DIN' }}
                        IconComponent={() =>
                            <DropdownArrow
                                marginRight={10}
                                width={24}
                                height={24}
                                direction="down"
                            />
                        }
                    >
                        <MenuItemPopOver value="no_amount_filter" disabled>Select an amount filter</MenuItemPopOver>
                        <MenuItemPopOver value="equal">Equal to</MenuItemPopOver>
                        <MenuItemPopOver value="greater_than">Greater than</MenuItemPopOver>
                        <MenuItemPopOver value="less_than">Less than</MenuItemPopOver>
                        <MenuItemPopOver value="between_two_numbers">Between two numbers</MenuItemPopOver>
                    </Select>
                </FormControl>

                <TextField
                    label="Amount"
                    variant="outlined"
                    value={`$${filter.amountValue1}`}
                    onChange={handleChangeAmountValue1}
                    placeholder="Enter a number..."
                    style={{ fontFamily: 'DIN' }}
                    InputProps={{ style: { fontFamily: 'DIN' } }}
                />

                {filter.amountModifier === 'between_two_numbers' && (
                    <TextField
                        label="Amount Upper Limit"
                        variant="outlined"
                        value={`$${filter.amountValue2}`}
                        onChange={handleChangeAmountValue2}
                        style={{ marginBottom: '10px', fontFamily: 'DIN' }}
                        InputProps={{ style: { fontFamily: 'DIN' } }}
                        placeholder="Enter a number..."
                    />
                )}
            </div>
        );
    };

    const renderPopoverContents = (filterOption: { name: string; title: string }) => {
        let body = null;
        switch (filterOption.name) {
            case 'brand':
                body = renderBrandFilter();
                break;
            case 'status':
                body = renderStatusFilter();
                break;
            case 'date':
                body = renderDateFilter();
                break;
            case 'amount':
                body = renderAmountFilter();
                break;
            default:
                body = null;
                break;
        }

        return (
            <>
                {body}
                {renderApplyButton()}
            </>
        );
    };

    const handleClosePopover = () => {
        setOpenPopoverIndex(null);
    };

    const handleClickFilterOption = (index: number) => (event: React.MouseEvent<HTMLDivElement>) => {
        setOpenPopoverIndex(openPopoverIndex === index ? null : index);
    };

    const renderApplyButton = () => {
        return (
            <>
                <ButtonSymphony
                    className="invoiceFilterApplyButton"
                    style={{ marginLeft: 'auto', marginRight: 'auto' }}
                    onClick={applyFilters}
                    disabled={isLoading}
                    isLoading={isLoading}
                    size="small"
                >
                    Apply
                </ButtonSymphony>
                <ButtonSymphony
                    className="invoiceFilterClearButton"
                    style={{ marginLeft: 'auto', marginRight: 'auto' }}
                    onClick={removeFilter}
                    variant="text"
                >
                    Clear
                </ButtonSymphony>
            </>
        );
    };

    return (
        <div className="invoiceFilterContainer" style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            paddingBottom: mobileView ? 24 : 16,
        }}>
            <div style={{ whiteSpace: 'nowrap' }}>Filter by:</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {INVOICE_FILTERS.map((filterOption, filterIndex) => {
                    const filterChipId = `${filterOption.name}-${filterIndex}`;
                    return (
                        <Fragment key={makeKey('key', filterChipId)}>
                            <Chip
                                id={filterChipId}
                                style={{
                                    backgroundColor: isFilterActive(filterOption.name) ? 'rgba(136, 0, 255, 0.08)' : '#EBEBEB',
                                    color: isFilterActive(filterOption.name) ? '#8800FF' : '#777',
                                }}
                                label={getFilterResultString(filterOption)}
                                onClick={handleClickFilterOption(filterIndex)}
                            />
                            <Popover
                                id={filterChipId}
                                open={openPopoverIndex === filterIndex}
                                anchorEl={document.getElementById(filterChipId)}
                                onClose={handleClosePopover}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                            >
                                <div className="popoverContents" style={{
                                    display: 'flex',
                                    gap: 8,
                                    padding: 16,
                                    flexDirection: 'column'
                                }}>
                                    {renderPopoverContents(filterOption)}
                                </div>
                            </Popover>
                        </Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export default InvoiceFilterOptions; 